import React, { useContext } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Text } from "./base/index";
import Article from "./Article";
import { PageStateContext } from "../PageContext";
import GlossaryTerms from "./GlossaryTerms";
import useGlossaryHandler from "../hooks/useGlossaryHandler";
import HiddenBlock from "./HiddenBlock";
import useConditionEvaluator from "../hooks/useConditionEvaluator";
import { debugLog } from "../utils/index";
import { createConditionStatusInfo } from "../utils/contentStatusUtils";
import { contentProps } from "./propTypes";

const Step = ({
  id,
  number,
  total,
  title = null,
  content = null,
  className = "",
  handleAnchor,
  isNested = false,
  fullWidth = false,
}) => {
  const { pageState } = useContext(PageStateContext);
  const { evaluateConditions } = useConditionEvaluator();

  if (!pageState || !pageState.uiText || pageState.uiText.length < 1) {
    return (
      <div className="header-loading">
        <span>Loading Step...</span>
      </div>
    );
  }

  const { uiText, isPreview } = pageState;
  const { glossaryIds } = useGlossaryHandler();

  // Evaluate conditions for the step's content
  const { shouldHide, conditionSummary } = evaluateConditions(content);

  // Debugging logs
  debugLog(
    `Step - ID: ${id || "N/A"}, Title: ${title ||
      "N/A"}, isPreview: ${isPreview}, shouldHide: ${shouldHide}, Conditions: ${conditionSummary}`
  );

  // Determine if conditions are set to apply border
  const conditionsExist = conditionSummary !== "None";

  // Create status info objects
  const conditionStatusInfo = createConditionStatusInfo(
    shouldHide,
    conditionSummary
  );

  // For step components, we typically don't have changedType/changedData directly
  // but we can check if conditions are set
  const statusInfo = conditionStatusInfo;

  // Determine wrapper classes
  const wrapperClassNames = [
    conditionsExist ? "edited-error tooltip" : "",
    shouldHide && isPreview ? "semi-transparent-red-bg" : "",
  ]
    .filter(Boolean)
    .join(" ");

  // Early return if step should be hidden and not in preview
  if (shouldHide && !isPreview) {
    // Add debugLog here with conditionSummary
    debugLog(
      `Hiding Step - ID: ${id || "N/A"}, Title: ${title ||
        "N/A"}, Conditions: ${conditionSummary}`
    );
    return null;
  }

  // If shouldHide but in preview mode, render with semi-transparent background
  // If conditions are set but not fully evaluable, log the conditionSummary
  if (shouldHide && isPreview) {
    debugLog(
      `Step may be hidden in normal mode - ID: ${id || "N/A"}, Title: ${title ||
        "N/A"}, Conditions: ${conditionSummary}`
    );
  } else if (conditionsExist && !shouldHide) {
    // Conditions are set but not fully evaluable or did not trigger hide
    debugLog(
      `Conditions set for Step - ID: ${id || "N/A"}, Title: ${title ||
        "N/A"}, Conditions: ${conditionSummary}`
    );
  }

  return (
    <div
      className={classNames({
        "mt-4": number === 1,
        "mt-10": number !== 1,
        "mb-10": number !== total,
        "break-before-avoid": number === 1,
      })}
    >
      <Text
        tag="label"
        className={classNames({
          "text-blue-accent mb-3 break-after-avoid": true,
          "break-before-avoid": number === 1,
        })}
      >
        {`${uiText.article_step} ${number} ${uiText.article_of} ${total}`}
      </Text>

      {(!isNested || fullWidth) && title && (
        <Text
          tag="h3"
          use="label"
          options={{ size: "text-body" }}
          className={
            statusInfo.status
              ? wrapperClassNames
              : `text-gray-2 mb-3 mt-3 break-after-avoid ${className}`
          }
        >
          {title}
          {statusInfo.status && (
            <span className="tooltiptext">{statusInfo.msg}</span>
          )}
        </Text>
      )}

      {content && (
        <div className={`${wrapperClassNames} break-before-avoid`}>
          {shouldHide && isPreview ? (
            <Article content={content} handleAnchor={handleAnchor} />
          ) : shouldHide && !isPreview ? (
            <HiddenBlock block={content} handleAnchor={handleAnchor} />
          ) : (
            <Article content={content} handleAnchor={handleAnchor} />
          )}
          {statusInfo.status && conditionsExist && (
            <span className="tooltiptext">{statusInfo.msg}</span>
          )}
        </div>
      )}

      <GlossaryTerms glossaryIds={glossaryIds} />
    </div>
  );
};

// Define PropTypes for Step component
Step.propTypes = {
  id: PropTypes.number.isRequired,
  number: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  title: PropTypes.string,
  className: PropTypes.string,
  content: contentProps,
  steps: PropTypes.arrayOf(PropTypes.object),
  handleAnchor: PropTypes.func.isRequired,
  isNested: PropTypes.bool,
  fullWidth: PropTypes.bool,
};

export default Step;